import { get } from 'aws-amplify/api';
import { fetchAuthSession, getCurrentUser } from 'aws-amplify/auth';

export const HEADER_DATA_CHANGE_EVENT = 'getwetheaderdatachange';

let cachedHeaderData = null;
let pendingPromise = null;

async function readJsonBody(body) {
    const text = await body.text();
    return text ? JSON.parse(text) : null;
}

async function fetchSessionUser(idToken) {
    const op = get({
        apiName: 'api',
        path: '/cognito/session-user',
        options: {
            headers: {
                Authorization: `Bearer ${idToken}`
            }
        }
    });

    const { body } = await op.response;
    const payload = await readJsonBody(body);
    return payload?.user || null;
}

async function fetchCurrentProfileUser(idToken) {
    const op = get({
        apiName: 'api',
        path: '/cognito/me',
        options: {
            headers: {
                Authorization: `Bearer ${idToken}`
            }
        }
    });

    const { body } = await op.response;
    const payload = await body.json();
    return payload?.user || payload || null;
}

async function fetchUnreadMessageCount(idToken) {
    const op = get({
        apiName: 'api',
        path: '/cognito/messages/summary',
        options: {
            headers: {
                Authorization: `Bearer ${idToken}`
            }
        }
    });

    const { body } = await op.response;
    const payload = await body.json();
    return Number(payload?.unread_message_count || 0);
}

async function fetchHeaderData() {
    let user = null;

    try {
        user = await getCurrentUser();
    } catch {
        return {
            user: null,
            sessionUser: null,
            idToken: null,
            unreadMessageCount: 0
        };
    }

    const session = await fetchAuthSession();
    const idToken = session.tokens?.idToken?.toString() || null;

    if (!idToken) {
        return {
            user,
            sessionUser: null,
            idToken: null,
            unreadMessageCount: 0
        };
    }

    const [sessionUser, profileUser, unreadMessageCount] = await Promise.all([
        fetchSessionUser(idToken),
        fetchCurrentProfileUser(idToken),
        fetchUnreadMessageCount(idToken)
    ]);

    return {
        user,
        sessionUser: {
            ...sessionUser,
            ...profileUser
        },
        idToken,
        unreadMessageCount
    };
}

export async function getHeaderData() {
    if (cachedHeaderData) {
        return cachedHeaderData;
    }

    if (pendingPromise) {
        return pendingPromise;
    }

    pendingPromise = fetchHeaderData()
        .then((data) => {
            cachedHeaderData = data;
            pendingPromise = null;
            return data;
        })
        .catch((err) => {
            pendingPromise = null;
            throw err;
        });

    return pendingPromise;
}

export function setHeaderDataCache(data) {
    cachedHeaderData = data;
}

export function updateHeaderDataCache(data) {
    cachedHeaderData = {
        ...(cachedHeaderData || {}),
        ...data
    };
    return cachedHeaderData;
}

export function clearHeaderCache() {
    cachedHeaderData = null;
}

export function notifyHeaderDataChanged(detail = cachedHeaderData) {
    window.dispatchEvent(new CustomEvent(HEADER_DATA_CHANGE_EVENT, { detail }));
}
